import { CalendarToday } from '@mui/icons-material';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@mui/material';
import React from 'react';
import { Line } from 'react-chartjs-2';
import { getChartOptions } from '../../utils/chart-helpers';
import SectionHeader from './SectionHeader';

export default function DailyStatsChart({
  dailyChartData,
  dailyData,
  loading,
  selectedDailyYear,
  selectedDailyMonth,
  availableYears,
  availableMonths,
  onDailyYearChange,
  onDailyMonthChange
}) {
  if (!dailyChartData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <Typography variant="h6" color="textSecondary">
          {loading
            ? 'Loading daily statistics...'
            : 'No daily data available'}
        </Typography>
      </Box>
    );
  }

  const currentMonth =
    dailyData?.monthName ||
    new Date().toLocaleDateString('en-US', { month: 'long' });
  const currentYear = dailyData?.year || new Date().getFullYear();

  const monthlyPackageTotal =
    dailyData?.packageDetails?.monthlyTotal ||
    dailyData?.packageDetails?.days?.reduce(
      (sum, day) => sum + (day.total || 0),
      0
    ) ||
    0;
  const monthlyShipmentTotal =
    dailyData?.zoeShipmentTracking?.monthlyTotal ||
    dailyData?.zoeShipmentTracking?.days?.reduce(
      (sum, day) => sum + (day.total || 0),
      0
    ) ||
    0;

  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <SectionHeader
          title={`Daily Statistics - ${currentMonth} ${currentYear}`}
          icon={<CalendarToday />}
        />
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="daily-year-select-label">Year</InputLabel>
            <Select
              labelId="daily-year-select-label"
              id="daily-year-select"
              value={selectedDailyYear}
              label="Year"
              onChange={onDailyYearChange}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="daily-month-select-label">Month</InputLabel>
            <Select
              labelId="daily-month-select-label"
              id="daily-month-select"
              value={selectedDailyMonth}
              label="Month"
              onChange={onDailyMonthChange}
            >
              {availableMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}
      >
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontSize: '0.8rem' }}
        >
          As of {currentDate}
        </Typography>
        <Box textAlign="right">
          <Typography
            variant="body2"
            color="primary"
            sx={{ fontWeight: 600, fontSize: '0.85rem' }}
          >
            Monthly Package Details: {monthlyPackageTotal.toLocaleString()}
          </Typography>
          <Typography
            variant="body2"
            color="error"
            sx={{ fontWeight: 600, fontSize: '0.85rem' }}
          >
            Monthly Shipment Tracking: {monthlyShipmentTotal.toLocaleString()}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 400 }}>
        <Line
          options={getChartOptions(dailyChartData)}
          data={dailyChartData}
        />
      </Box>
    </Paper>
  );
}
